import { useState } from 'react';
import { cn } from '@/lib/utils';

const SIZES = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-11 w-11 text-sm',
  lg: 'h-20 w-20 text-2xl',
} as const;

function initials(name: string) {
  const parts = name.trim().split(/\s+/);
  const letters = parts.length > 1 ? [parts[0], parts[parts.length - 1]] : parts;
  return letters.map((part) => part[0]).join('').toUpperCase();
}

// A circular avatar with a graceful fallback to initials — GitHub's `.png` avatar
// endpoint 404s for a deleted/renamed account, and a hacker with no GitHub at all has
// no avatarUrl to begin with.
export function AvatarCircle({
  src,
  name,
  size = 'md',
  className,
}: {
  src: string | null;
  name: string;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);

  const base = cn(
    'shrink-0 overflow-hidden rounded-full ring-1 ring-border',
    SIZES[size],
    className,
  );

  if (!src || failed) {
    return (
      <div
        className={cn(
          base,
          'flex items-center justify-center bg-muted font-semibold text-muted-foreground',
        )}
      >
        {initials(name) || '?'}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      className={cn(base, 'object-cover')}
      onError={() => setFailed(true)}
    />
  );
}
